import { Box } from "@ui/box/Box";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { Dialog } from "@ui/dialog/Dialog";
import {
  getDefaultLightTheme,
  GlobalTheme,
  ITheme,
} from "@ui/theme/GlobalTheme";
//import { useCreateRouter } from "badmfck-presenter";
import { s_invoke, s_subscribe } from "badmfck-signal";
import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { useCreateRouter } from "./services/Router";
import { sitemap } from "./Sitemap";
import { getAppVersionLabel } from "src/utils/appVersion";
import { GlobalView } from "./view/GlobalView";
import { handleUniversalDialogSystemBack } from "./view/components/universalDialog/signals";
import { handleRegisteredOverlaySystemBack } from "@ui/utils/systemBack";
import { getNativeAppLinkTarget } from "./utils/appLinks";
import { DesignV2Style, V2 } from "@ui/theme/DesignV2";

const themeLight = getDefaultLightTheme();

export const designV2Theme = themeLight;

themeLight.colors.backgroundPrimary = V2.canvas;
themeLight.colors.textPrimary = V2.ink;
themeLight.colors.textSecondary = V2.muted;
// CSS variables so a workspace skin (e.g. private) recolours primary buttons too.
themeLight.colors.buttonBackgroundPrimary = "var(--ds-accent, #2B5C8A)";
themeLight.colors.buttonBackgroundPrimaryHover = "var(--ds-accent-ink, #1D3F61)";
themeLight.colors.inputBorderPrimary = V2.border;

function App() {
  const router = useCreateRouter(sitemap(), <GlobalView />);
  const [lang, setLang] = useState<string>("en");
  const [theme] = useState<ITheme>(themeLight);
  const [, setAppLinkRevision] = useState(0);

  useEffect(() => {
    s_subscribe((l) => {
      setLang(l);
    }, "LANG_CHANGED");
  }, [lang]);

  useEffect(() => {
    s_invoke({ with: window.innerWidth, height: window.innerHeight }, "resize");
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let backListener: PluginListenerHandle | undefined;
    let appUrlListener: PluginListenerHandle | undefined;
    let disposed = false;

    const openAppLink = (url: string) => {
      const target = getNativeAppLinkTarget(url);
      if (!target) return;

      if (window.location.pathname + window.location.search + window.location.hash !== target) {
        window.history.replaceState({}, "", target);
      }
      setAppLinkRevision(revision => revision + 1);
    };

    void CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      if (handleRegisteredOverlaySystemBack()) return;
      if (handleUniversalDialogSystemBack()) return;
      if (canGoBack) {
        window.history.back();
        return;
      }
      void CapacitorApp.exitApp();
    }).then((handle) => {
      if (disposed) {
        void handle.remove();
        return;
      }
      backListener = handle;
    });

    void CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      openAppLink(url);
    }).then((handle) => {
      if (disposed) {
        void handle.remove();
        return;
      }
      appUrlListener = handle;
    });

    void CapacitorApp.getLaunchUrl().then((launch) => {
      if (!disposed && launch?.url) openAppLink(launch.url);
    });

    return () => {
      disposed = true;
      void backListener?.remove();
      void appUrlListener?.remove();
    };
  }, []);

  return (
    <>
      <GlobalTheme theme={theme}>
        <DesignV2Style />
        {router}
        <Box
          sx={{
            position: "fixed",
            bottom: "10px",
            right: "10px",
            fontSize: "9px",
            opacity: ".55",
            padding: "4px 6px",
            backgroundColor: "rgba(255,255,255,0.9)",
            borderRadius: "6px",
            border: "1px solid rgba(0,0,0,0.06)",
            zIndex: 20,
            "@media (max-width: 768px)": { display: "none" },
          }}
        >
          {getAppVersionLabel()}
        </Box>
        <Dialog />
        <ToastContainer
          position="top-center"
          newestOnTop
          limit={3}
          closeOnClick
          pauseOnHover
          style={{
            top: "max(12px, var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
            left: "50%",
            right: "auto",
            width: "min(360px, calc(100vw - 32px))",
            padding: 0,
            transform: "translateX(-50%)",
          }}
          toastStyle={{
            minHeight: "54px",
            marginBottom: "10px",
            borderRadius: "16px",
            border: "1px solid rgba(226, 232, 240, 0.96)",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.16)",
            overflow: "hidden",
          }}
        />
      </GlobalTheme>
    </>
  );
}

//<AuthView/>

export default App;
